<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		removeInterval,
		saveInterval,
		savePowerPrices,
	} from '../../../helpers/power_prices_helpers';
	import { capFirstChar, sortOn } from '../../../helpers/utilities';
	import { getTimeFormat } from 'src/helpers/date_helper';
	// Design system components
	import {
		Button,
		CardGrid,
		AdvancedCard,
		AdvancedCardV2,
		ActionsContainer,
		IconButton,
		Modal,
		Text,
		Alert,
		toast,
		ControlsContainer,
		Container,
		Icon,
		disableWrapperFocus,
	} from '@Shelly/shelly-design-system';
	//App Components
	import AddEditIntervalWizard from './AddEditIntervalWizard.svelte';
	import WeekDaysSelected from '../../TimePicker/WeekDaysSelected.svelte';
	import TariffIntervalsChart from '../../../components/Chart/TariffIntervalsChart.svelte';
	//Types
	import type { PowerPricesData, PowerPricesInterval } from '../../../types/PowerPrices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let powerPrices: PowerPricesData,
		currency = '',
		liveTariffEnabled;

	let showModal = false,
		removeIntervalModal = false,
		maxIntervals = 20, //increased with https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2034
		editedInterval: PowerPricesInterval = null,
		waitForResponse = false;

	$: disabled =
		liveTariffEnabled ||
		(powerPrices.schedule.length > 0 &&
			powerPrices.schedule.flat().find((a) => a == 0) === undefined) ||
		Object.keys(powerPrices.tariffs).length == 0 ||
		Object.keys(powerPrices.intervals).length === 20;

	const editInterval = (interval: PowerPricesInterval) => {
			showModal = true;
			editedInterval = { ...interval };
		},
		addInterval = () => {
			const intervals = Object.keys(powerPrices.intervals);
			if (intervals.length <= maxIntervals) {
				let id = 0;
				for (let i = 1; i <= maxIntervals; i++) {
					if (powerPrices.intervals[i] == null) {
						id = i;
						break;
					}
				}
				editInterval({
					id,
					tariffID: 0,
					days: [0, 1, 2, 3, 4, 5, 6],
					from: '00:00',
					to: '00:00',
					create: Date.now(),
				});
			} else {
				toast.open({
					type: 'danger',
					title: $format('tariff_intervals_limit_alert_message', {
						values: { maxIntervals },
						default: `You have reached the limit of ${maxIntervals} tariff intervals.`,
					}),
				});
			}
		},
		showDeleteIntervalPrompt = (interval: PowerPricesInterval) => {
			removeIntervalModal = true;
			editedInterval = { ...interval };
		},
		remove = () => {
			let toastMsg = $format('interval_deleted_succ', {
				default: 'Interval deleted successfully.',
			});
			setPowerPrices(removeInterval(editedInterval, powerPrices), toastMsg);
		},
		setPowerPrices = async (powerPricesData: PowerPricesData, toastMsg = '') => {
			waitForResponse = true;
			const res = await savePowerPrices(powerPricesData, true, toastMsg);
			if (res.isok) {
				powerPrices = powerPricesData;
				showModal = false;
				removeIntervalModal = false;
			}
			waitForResponse = false;
		},
		save = () => {
			try {
				const powerPricesData: PowerPricesData = saveInterval(editedInterval, powerPrices);

				let toastMsg = $format('interval_saved_succ', {
					default: 'Interval added successfully!',
				});
				setPowerPrices(powerPricesData, toastMsg);
			} catch (e) {
				toast.open({
					type: 'info',
					title: $format(`alert_error_interval_overlap`, {
						default: 'This interval overlaps with another interval!',
					}),
				});
			}
		};

	$: if (!showModal && editedInterval?.edit) {
		editedInterval = {
			...editedInterval,
			edit: false,
		};
	}
</script>

<Text element="h2" class="mb-s">
	{$format('title_intervals', { default: 'Intervals' })}
</Text>
{#if Object.keys(powerPrices.intervals).length == 0}
	<Alert
		class="mb-s"
		type="info"
		heading={capFirstChar(
			$format('create_your_intervals', {
				default: 'Create Your Intervals',
			}).toLowerCase(),
		)}
		isHeadingBold
		message={$format('missing_intervals_alert', {
			default: 'Currently you don’t have any intervals created. Add an interval to get started.',
		})}
	/>
{:else}
	{#if !powerPrices.schedule.flat().every((i) => i === 0)}
		<ControlsContainer class="mb-s" color={$showNewLayout ? 'color-1' : 'color-2'}>
			<svelte:fragment slot="first">
				<TariffIntervalsChart {powerPrices} {currency} />
			</svelte:fragment>
		</ControlsContainer>
	{/if}
	<CardGrid gridGap="s" lg={250}>
		{@const intervals = sortOn(Object.values(powerPrices.intervals), 'create')}
		{#each intervals as interval}
			{@const tariff = powerPrices.tariffs[interval.tariffID]}
			{@const avatar = tariff?.bgColor
				? `text-${tariff.bgColor} fat fa-calendar`
				: 'text-color-green fat fa-calendar'}
			{#key interval.days}
				<div use:disableWrapperFocus>
					{#if $showNewLayout}
						<AdvancedCardV2
							class="width-full"
							title={$format('price_period', { default: 'Price period' })}
							hideSlotTitle={false}
							avatar={{ icon: avatar }}
							subtitle={{
								value: `${getTimeFormat.apply(
									interval,
									interval.from.split(':'),
								)} - ${getTimeFormat.apply(interval, interval.to.split(':'))}`,
								bold: false,
							}}
							description={{
								value: `${tariff?.name} / ${tariff?.price} ${currency}`,
								textSmall: false,
								bold: true,
							}}
						>
							<WeekDaysSelected bind:selection={interval.days} slot="title" />

							<ActionsContainer orientation="vertical" type="simple" slot="right">
								<IconButton
									aria-label={$format('button_edit', {
										default: 'Edit interval',
									})}
									icon="fas fa-pen"
									slot="first"
									variant="primary"
									on:click={() => {
										editInterval(interval);
										editedInterval.edit = true;
									}}
								/>
								<IconButton
									aria-label={$format('button_delete', {
										default: 'Delete interval',
									})}
									class="align-self-center"
									icon="fas fa-trash"
									variant="danger"
									slot="second"
									on:click={() => showDeleteIntervalPrompt(interval)}
								/>
							</ActionsContainer>
						</AdvancedCardV2>
					{:else}
						<AdvancedCard
							class="width-full"
							title={$format('price_period', { default: 'Price period' })}
							hideSlotTitle={false}
							avatar={{ icon: avatar }}
							subtitle={{
								value: `${getTimeFormat.apply(
									interval,
									interval.from.split(':'),
								)} - ${getTimeFormat.apply(interval, interval.to.split(':'))}`,
								bold: false,
							}}
							description={{
								value: `${tariff?.name} / ${tariff?.price} ${currency}`,
								textSmall: false,
								bold: true,
							}}
						>
							<WeekDaysSelected bind:selection={interval.days} slot="title" />

							<ActionsContainer orientation="vertical" type="simple" slot="right">
								<IconButton
									aria-label={$format('button_edit', {
										default: 'Edit interval',
									})}
									icon="fas fa-pen"
									slot="first"
									variant="primary"
									on:click={() => {
										editInterval(interval);
										editedInterval.edit = true;
									}}
								/>
								<IconButton
									aria-label={$format('button_delete', {
										default: 'Delete interval',
									})}
									class="align-self-center"
									icon="fas fa-trash"
									variant="danger"
									slot="second"
									on:click={() => showDeleteIntervalPrompt(interval)}
								/>
							</ActionsContainer>
						</AdvancedCard>
					{/if}
				</div>
			{/key}
		{/each}
	</CardGrid>
{/if}

<Button on:click={addInterval} size="m" class="width-md-auto width-sm-full" {disabled}>
	{$format('button_add_interval', { default: 'Add Interval' })}
</Button>

{#if showModal}
	<AddEditIntervalWizard
		bind:editedInterval
		bind:powerPrices
		bind:showModal
		bind:waitForResponse
		{currency}
		on:finish={save}
	/>
{/if}
{#if removeIntervalModal}
	<Modal
		showModal={removeIntervalModal}
		on:close={() => (removeIntervalModal = false)}
		title={`${capFirstChar(
			$format('button_delete', { default: 'Delete' }),
		)} ${capFirstChar($format('title_tariff'))} ${capFirstChar($format('process_interval'))}?`}
	>
		<Container slot="content" padding="0">
			<div class="d-flex flex-direction-column align-items-center">
				<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
				<Text class="width-full">
					{$format('delete_itm_general_msg', {
						values: {
							type: $format('process_tariff_interval', {
								default: 'tariff interval',
							}),
						},
						default: `Deleting this ${$format('process_tariff_interval', {
							default: 'tariff interval',
						})} will permanently remove it from your account.`,
					})}
				</Text>
			</div>
		</Container>

		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (removeIntervalModal = false)}
				>{$format('button_cancel', { default: 'Cancel' })}</Button
			>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
				loading={waitForResponse}
				on:click={remove}
			>
				{capFirstChar($format('button_delete', { default: 'Delete' }))}
				{capFirstChar($format('process_interval')).toLowerCase()}
			</Button>
		</div>
	</Modal>
{/if}
